import { SVGProps } from 'react'

import { useAuth } from '@clerk/nextjs'
import { DiscordLogoIcon as DiscordIcon } from '@radix-ui/react-icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { animationOpacityIn } from 'style'

import { dashboardPaths, websitePaths } from 'domains/website/website.paths'
import {
  discordLink,
  githubLink,
  linkedinLink,
  twitterLink,
  youtubeLink,
} from 'lib/constants/social'
import { replaceDynamicPaths } from 'lib/routes/replace-dynamic-paths'

import Box from 'components/atomic/atoms/box'
import { IconStyled, RadixIcon } from 'components/atomic/atoms/icon'
import { GithubIcon } from 'components/atomic/atoms/icons/logos/github'
import { LinkedinIcon } from 'components/atomic/atoms/icons/logos/linkedin'
import { TwitterIcon } from 'components/atomic/atoms/icons/logos/twitter'
import { YoutubeIcon } from 'components/atomic/atoms/icons/logos/youtube'
import Logo from 'components/atomic/atoms/logo'
import { LogoProps } from 'components/atomic/atoms/logo/logo-squared'
import Text from 'components/atomic/atoms/text'
import ThemeSelect from 'components/pages/@shared/theme-select'

type ColumnLinkType = {
  name: string
  href: string
  isExternal?: boolean
  hidden?: boolean
}

type SocialLink = {
  href: string
  icon:
    | RadixIcon
    | ((props: SVGProps<SVGSVGElement>) => JSX.Element)
    | ((props: LogoProps) => JSX.Element)

  alt: string
}

const ColumnLink = (props: ColumnLinkType) => {
  const { isExternal, name, href } = props

  if (isExternal) {
    return (
      <Text
        as="a"
        color="text3"
        colorHover="text1"
        size="sm"
        href={href}
        target="_blank"
        rel="noopener"
        weight="medium"
        css={{ animation: animationOpacityIn }}
      >
        {name}
      </Text>
    )
  }

  return (
    <Text
      as={Link}
      href={href}
      color="text3"
      colorHover="text1"
      size="sm"
      weight="medium"
      css={{ animation: animationOpacityIn }}
    >
      {name}
    </Text>
  )
}

const Footer = () => {
  const { isSignedIn } = useAuth()
  const { query } = useRouter()
  const account = query.account as string

  const columns: { title: string; links: ColumnLinkType[] }[] = [
    {
      title: 'Product',
      links: [
        {
          name: isSignedIn ? 'Dashboard' : 'Sign Up',
          href: isSignedIn ? dashboardPaths.dashboard : dashboardPaths.register,
        },
        {
          name: 'Documentation',
          href: websitePaths.docs,
        },
        {
          name: 'Templates',
          href: websitePaths.templates,
        },
        {
          name: 'CLI',
          href: websitePaths.cli,
        },
        {
          name: 'Changelog',
          href: websitePaths.changelog,
        },
        {
          name: 'Guides',
          href: websitePaths.guides,
        },
        {
          name: 'Roadmap',
          href: websitePaths.roadmap,
        },
      ],
    },
    {
      title: 'Resources',
      links: [
        {
          name: 'System Status',
          href: 'https://status.grafbase.com/',
          isExternal: true,
        },
        {
          name: 'Privacy Policy',
          href: websitePaths.privacy,
        },
        {
          name: 'Terms of Use',
          href: websitePaths.terms,
        },
        {
          name: 'Fair Use Policy',
          href: websitePaths.fairUse,
        },
      ],
    },
    {
      title: 'Frameworks',
      links: [
        {
          name: 'Next.js',
          href: replaceDynamicPaths(websitePaths.framework, 'nextjs'),
        },
        {
          name: 'Nuxt',
          href: replaceDynamicPaths(websitePaths.framework, 'nuxt'),
        },
        {
          name: 'SvelteKit',
          href: replaceDynamicPaths(websitePaths.framework, 'sveltekit'),
        },
        {
          name: 'Remix',
          href: replaceDynamicPaths(websitePaths.framework, 'remix'),
        },
        {
          name: 'SolidJS',
          href: replaceDynamicPaths(websitePaths.framework, 'solidjs'),
        },
        {
          name: 'Deno',
          href: replaceDynamicPaths(websitePaths.framework, 'deno'),
        },
        {
          name: 'Fresh',
          href: replaceDynamicPaths(websitePaths.framework, 'fresh'),
        },
        {
          name: 'Swift',
          href: replaceDynamicPaths(websitePaths.framework, 'swift'),
        },
        {
          name: 'Qwik',
          href: replaceDynamicPaths(websitePaths.framework, 'qwik'),
        },
        {
          name: 'React',
          href: replaceDynamicPaths(websitePaths.framework, 'react'),
        },
        {
          name: 'Kotlin',
          href: replaceDynamicPaths(websitePaths.framework, 'kotlin'),
        },
        {
          name: 'Vue',
          href: replaceDynamicPaths(websitePaths.framework, 'vue'),
        },
      ],
    },
    {
      title: 'Company',
      links: [
        {
          name: 'About',
          href: websitePaths.about,
        },
        {
          name: 'Blog',
          href: websitePaths.blog,
        },
        {
          name: 'Careers',
          href: websitePaths.careers,
        },
        {
          name: 'Community',
          href: websitePaths.community,
        },
        {
          name: 'Contact Us',
          href: websitePaths.contact,
        },
        {
          name: 'Partners',
          href: 'https://grafbase.partnerportal.io',
          isExternal: true,
        },
        {
          name: 'Pricing',
          href: websitePaths.pricing,
        },
        {
          name: 'Security',
          href: websitePaths.security,
        },
      ],
    },
  ]

  const socialLinks: SocialLink[] = [
    {
      href: twitterLink,
      alt: 'Follow us on Twitter',
      icon: TwitterIcon,
    },
    {
      href: linkedinLink,
      alt: 'Follow us on Linkedin',
      icon: LinkedinIcon,
    },
    {
      href: githubLink,
      alt: 'Follow us on GitHub',
      icon: GithubIcon,
    },
    {
      href: discordLink,
      alt: 'Chat with us on Discord',
      icon: DiscordIcon,
    },
    {
      href: youtubeLink,
      alt: 'Subscribe to us on YouTube',
      icon: YoutubeIcon,
    },
  ]

  return (
    <Box>
      <Box
        as="footer"
        css={{ pt: '$18', pb: '$20', borderTop: '1px solid $bg3' }}
      >
        <Box
          css={{
            container: 'xl',
          }}
        >
          <Box
            as="nav"
            role="navigation"
            justify={{ '@initial': 'center', '@sm': 'between' }}
            flex={{ '@initial': 'column', '@sm': true }}
            css={{
              spaceY: 10,
              '@md': { spaceY: 0 },
            }}
          >
            <Box css={{ flex: 0.3 }}>
              <Text
                as={Link}
                href={websitePaths.homepage}
                color="text1"
                aria-label="Open Home page"
              >
                <Logo.WithText size="lg" />
              </Text>
              <Box flex items="center" css={{ mt: '$6', spaceX: 5 }}>
                {socialLinks.map((props, index) => (
                  <Text
                    as="a"
                    key={index}
                    href={props.href}
                    title={props.alt}
                    aria-label={props.alt}
                    target="_blank"
                    rel="noopener"
                  >
                    <IconStyled
                      as={props.icon}
                      size="xl"
                      color="text2"
                      colorHover="text1"
                    />
                  </Text>
                ))}
              </Box>

              <Box css={{ py: 16, maxWidth: 200 }}>
                <ThemeSelect />
              </Box>
            </Box>

            <Box
              grid
              css={{
                flex: 0.7,
                gridCols: 2,
                gap: '$8',
                '@sm': { gridCols: 4 },
              }}
            >
              {columns.map(({ title, links }, index) => (
                <Box
                  key={index}
                  flex="column"
                  css={{
                    spaceY: 4,
                  }}
                >
                  <Text as="h6" size="sm" weight="semiBold">
                    {title}
                  </Text>
                  <Box as="ul" css={{ spaceY: 2 }}>
                    {links
                      .filter(({ hidden }) => !hidden)
                      .map((props, index) => (
                        <li key={index}>
                          <ColumnLink {...props} />
                        </li>
                      ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box css={{ height: 1, width: '$full', bgc: '$bg2', mt: '$18' }} />
          <Box flex items="center" justify="between" css={{ mt: '$4' }}>
            <Text as="small" color="text2" weight="medium">
              &copy; Grafbase, Inc.
            </Text>
            {!!account && (
              <Box>
                <ThemeSelect size="sm" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Footer
